import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { api, thumbUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Leaflet's default icon URLs break under bundlers — inline a compact orange pin
// that also fits the site's aesthetic. Uses a data URI to avoid asset shipping.
const pinSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="46" viewBox="0 0 32 46">
  <path d="M16 0C7.163 0 0 7.163 0 16c0 11 16 30 16 30s16-19 16-30C32 7.163 24.837 0 16 0z" fill="#FF6A00" stroke="#000" stroke-width="2"/>
  <circle cx="16" cy="16" r="6" fill="#000"/>
</svg>`;
const pinIcon = new L.DivIcon({
  html: pinSvg,
  className: "pm-pin",
  iconSize: [32, 46],
  iconAnchor: [16, 46],
  popupAnchor: [0, -40],
});

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

// Centre of Great Britain — pans naturally regardless of pin distribution
const UK_CENTER = [54.0, -2.5];
const UK_ZOOM = 6;

function ClickCapture({ onClick, enabled }) {
  useMapEvents({
    click(e) {
      if (enabled) onClick(e.latlng);
    },
  });
  return null;
}

export default function EventsMap({ events }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const [coords, setCoords] = useState({}); // { trackName: {lat, lon, display_name} | null }
  const [loading, setLoading] = useState(false);
  const [pinMode, setPinMode] = useState(null); // trackName being placed
  const [reloadKey, setReloadKey] = useState(0);

  // Deduplicated list of tracks currently on screen
  const tracks = useMemo(() => {
    const set = new Set();
    for (const ev of events) {
      if (ev.track) set.add(ev.track);
    }
    return Array.from(set);
  }, [events]);

  // Group events by track for popup rendering
  const eventsByTrack = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const t = ev.track || "";
      if (!map[t]) map[t] = [];
      map[t].push(ev);
    }
    Object.values(map).forEach((arr) => arr.sort((a, b) => new Date(b.date) - new Date(a.date)));
    return map;
  }, [events]);

  useEffect(() => {
    if (tracks.length === 0) {
      setCoords({});
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/geocode/tracks", { tracks });
        if (!cancelled) setCoords(data || {});
      } catch (e) {
        if (!cancelled) setCoords({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tracks, reloadKey]);

  const handleMapClick = async (latlng) => {
    if (!pinMode) return;
    try {
      await api.put("/admin/geocode/manual", {
        track: pinMode,
        lat: latlng.lat,
        lon: latlng.lng,
        display_name: `${pinMode} (manual)`,
      });
      toast.success(`Pinned ${pinMode}`);
      setPinMode(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not save pin");
    }
  };

  const handleClearPin = async (track) => {
    if (!isAdmin) return;
    if (!confirm(`Remove pin for ${track}?`)) return;
    try {
      await api.delete(`/admin/geocode/manual/${encodeURIComponent(track)}`);
      toast.success(`Cleared ${track}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not clear pin");
    }
  };

  const mapped = tracks.filter((t) => coords[t]);
  const unmapped = tracks.filter((t) => !coords[t]);

  return (
    <div data-testid="events-map-view" className="space-y-6">
      {pinMode && (
        <div className="border border-[var(--pm-orange)] bg-[#1a0a00] p-4 flex items-center justify-between" data-testid="events-map-pin-mode-banner">
          <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">
            Click anywhere on the map to pin <span className="text-white">{pinMode}</span>
          </p>
          <button
            onClick={() => setPinMode(null)}
            className="text-xs uppercase tracking-[0.3em] text-white/60 hover:text-white border border-white/20 px-3 py-2"
            data-testid="events-map-cancel-pin"
          >
            Cancel
          </button>
        </div>
      )}
      <div className="border border-white/10 bg-[#0a0a0a] overflow-hidden">
        <div className={`h-[600px] w-full relative ${pinMode ? "cursor-crosshair" : ""}`}>
          <MapContainer
            center={UK_CENTER}
            zoom={UK_ZOOM}
            style={{ height: "100%", width: "100%", background: "#0a0a0a" }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickCapture onClick={handleMapClick} enabled={!!pinMode} />
            {mapped.map((track) => {
              const c = coords[track];
              const evs = eventsByTrack[track] || [];
              return (
                <Marker key={track} position={[c.lat, c.lon]} icon={pinIcon}>
                  <Popup className="pm-popup">
                    <div className="pm-popup-inner">
                      <p className="pm-popup-track">{track}</p>
                      <p className="pm-popup-count">{evs.length} {evs.length === 1 ? "event" : "events"}</p>
                      <ul className="pm-popup-events">
                        {evs.slice(0, 5).map((ev) => (
                          <li key={ev.id}>
                            <Link to={`/event/${ev.id}`} className="pm-popup-link">
                              {ev.cover_image_id && (
                                <img src={thumbUrl(ev.cover_image_id)} alt="" className="pm-popup-thumb" />
                              )}
                              <span className="pm-popup-name">
                                <span className="pm-popup-title">{ev.name || "Round"}</span>
                                <span className="pm-popup-date">{fmtDate(ev.date)} · {ev.photo_count || 0} photos</span>
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                      {evs.length > 5 && (
                        <p className="pm-popup-more">+ {evs.length - 5} more</p>
                      )}
                      {isAdmin && (
                        <div className="pm-popup-admin">
                          <button onClick={() => setPinMode(track)} className="pm-popup-adminbtn">Move pin</button>
                          <button onClick={() => handleClearPin(track)} className="pm-popup-adminbtn pm-popup-adminbtn-danger">Clear</button>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
          {loading && (
            <div className="absolute top-4 right-4 bg-black/80 text-white text-xs uppercase tracking-[0.2em] px-3 py-2 border border-[var(--pm-orange-40)] z-[1000] font-condensed" data-testid="events-map-loading">
              Geocoding…
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs font-condensed" data-testid="events-map-summary">
        <div className="border border-white/10 bg-[#0a0a0a] p-4">
          <p className="text-white/40 uppercase tracking-[0.2em]">Tracks on map</p>
          <p className="font-display text-3xl uppercase pm-orange mt-1">{mapped.length}</p>
        </div>
        <div className="border border-white/10 bg-[#0a0a0a] p-4">
          <p className="text-white/40 uppercase tracking-[0.2em]">Events shown</p>
          <p className="font-display text-3xl uppercase mt-1">{events.length}</p>
        </div>
        <div className="border border-white/10 bg-[#0a0a0a] p-4">
          <p className="text-white/40 uppercase tracking-[0.2em]">Unmapped tracks</p>
          <p className="font-display text-3xl uppercase text-white/50 mt-1">{unmapped.length}</p>
        </div>
      </div>

      {unmapped.length > 0 && (
        <div className="border border-white/10 bg-[#0a0a0a] p-5" data-testid="events-map-unmapped">
          <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold mb-3">
            Couldn&apos;t place on the map
          </p>
          <p className="text-xs text-white/40 font-condensed mb-3">
            OpenStreetMap didn&apos;t recognise these track names. Try tweaking the spelling
            in Admin → Events (e.g. add &quot;circuit&quot; or the county) to help geocoding.
          </p>
          <ul className="flex flex-wrap gap-2">
            {unmapped.map((t) => (
              <li key={t} className="flex items-center gap-2 text-xs text-white/60 border border-white/10 px-3 py-2 font-condensed">
                <span>{t} <span className="text-white/30">· {(eventsByTrack[t] || []).length} events</span></span>
                {isAdmin && (
                  <button
                    onClick={() => setPinMode(t)}
                    className="text-[10px] uppercase tracking-[0.2em] pm-orange border border-[var(--pm-orange-40)] hover:bg-[var(--pm-orange)] hover:text-black px-2 py-1 font-semibold"
                    data-testid={`events-map-pin-${t}`}
                  >
                    Pin on map
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
